import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Link2, UserCheck, Loader2, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UnlinkedCertsDialog = ({ open, onOpenChange, authHeaders, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [linkingId, setLinkingId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/certificate-requests/unlinked`, { headers: authHeaders() });
      setItems(res.data || []);
    } catch {
      toast.error('未紐付け修了証の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  const linkTo = async (certId, userId, userName) => {
    setLinkingId(certId);
    try {
      await axios.put(`${API}/admin/certificate-requests/${certId}/assign-user`, { userId }, { headers: authHeaders() });
      toast.success(`${userName} に紐付けました`);
      setItems(prev => prev.filter(i => i.id !== certId));
      onChanged && onChanged();
    } catch {
      toast.error('紐付けに失敗しました');
    } finally {
      setLinkingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="unlinked-certs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-[#00A651]" />未紐付け修了証の一括リンク
          </DialogTitle>
          <DialogDescription>
            登録ユーザーに紐付いていない修了証を、氏名・メール一致の候補にワンクリックで紐付けできます。
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600" data-testid="unlinked-count">未紐付け: {items.length}件</span>
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="unlinked-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1" />更新
          </Button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />読み込み中...
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm" data-testid="unlinked-empty">
              未紐付けの修了証はありません 🎉
            </div>
          ) : items.map(c => (
            <div key={c.id} className="border rounded-lg p-3 bg-white" data-testid={`unlinked-item-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{c.userName || '（氏名なし）'}</p>
                  <p className="text-xs text-gray-500 truncate">{c.userEmail || '（メールなし）'}</p>
                  <p className="text-xs text-gray-400 truncate mt-0.5">{c.courseName} {c.certificateNumber ? `・${c.certificateNumber}` : ''}</p>
                </div>
                <Badge className={c.status === 'completed' ? 'bg-green-100 text-green-700 shrink-0' : 'bg-yellow-100 text-yellow-700 shrink-0'}>
                  {c.status === 'completed' ? '発行済み' : c.status}
                </Badge>
              </div>
              <div className="mt-2 border-t pt-2">
                {c.candidates && c.candidates.length > 0 ? (
                  <div className="space-y-1">
                    {c.candidates.length === 1 && (
                      <p className="text-[11px] text-[#00A651] mb-1">候補が1人だけ見つかりました</p>
                    )}
                    {c.candidates.length > 1 && (
                      <p className="text-[11px] text-orange-500 mb-1">複数の候補があります。正しいユーザーを選んでください。</p>
                    )}
                    {c.candidates.map(u => (
                      <div key={u.id} className="flex items-center justify-between gap-2 bg-gray-50 rounded px-2 py-1.5">
                        <div className="min-w-0">
                          <p className="text-xs font-medium truncate">{u.name}</p>
                          <p className="text-[11px] text-gray-500 truncate">{u.email}{u.phone ? `・${u.phone}` : ''}</p>
                        </div>
                        <Button size="sm" className="bg-[#00A651] hover:bg-[#008a43] h-7 text-xs shrink-0"
                          disabled={linkingId === c.id}
                          data-testid={`link-btn-${c.id}-${u.id}`}
                          onClick={() => linkTo(c.id, u.id, u.name)}>
                          {linkingId === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><UserCheck className="h-3 w-3 mr-1" />紐付け</>}
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">一致する登録ユーザーが見つかりません（手動で紐付けてください）</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UnlinkedCertsDialog;
